import { useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useEvents } from "@/hooks/useConflictData";
import { Target, Lock, Zap, AlertTriangle, Clock, Radius } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import PricingDialog from "@/components/PricingDialog";

function haversine(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Missile type estimates based on known conflict zone weapons
const MISSILE_PROFILES = [
  { type: "Iskander-M", range: 500, speed: 2100, blastRadius: 3, shelterTime: "15-30 sec" },
  { type: "Kalibr", range: 1500, speed: 900, blastRadius: 2, shelterTime: "60-90 sec" },
  { type: "Shahed-136 (Drone)", range: 2000, speed: 185, blastRadius: 0.5, shelterTime: "Several min" },
  { type: "Kh-101", range: 4500, speed: 700, blastRadius: 2.5, shelterTime: "60-90 sec" },
  { type: "Qassam", range: 40, speed: 200, blastRadius: 0.3, shelterTime: "15 sec" },
  { type: "Fajr-5", range: 75, speed: 300, blastRadius: 0.5, shelterTime: "30 sec" },
];

const MissileEarlyWarning = ({ embedded }: { embedded?: boolean }) => {
  const [pricingOpen, setPricingOpen] = useState(false);
  const { isPremium } = useAuth();
  const { data: events } = useEvents(undefined, isPremium);

  const missileEvents = useMemo(() => {
    if (!events?.length) return [];
    return events
      .filter((e) => (e.event_type === "missile" || e.event_type === "drone") && e.lat && e.lng)
      .sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime())
      .slice(0, 20);
  }, [events]);

  const stats = useMemo(() => {
    if (!missileEvents.length) return null;
    const now = Date.now();
    const last6h = missileEvents.filter(
      (e) => now - new Date(e.occurred_at).getTime() < 6 * 3600000
    );
    const last24h = missileEvents.filter(
      (e) => now - new Date(e.occurred_at).getTime() < 24 * 3600000
    );

    // Cluster detection: if multiple events within 50km and 1 hour = potential salvo
    const clusters: { count: number; center: string; time: string }[] = [];
    const used = new Set<string>();
    for (const e of last6h) {
      if (used.has(e.id)) continue;
      const cluster = last6h.filter(
        (o) =>
          !used.has(o.id) &&
          haversine(e.lat!, e.lng!, o.lat!, o.lng!) < 50 &&
          Math.abs(new Date(e.occurred_at).getTime() - new Date(o.occurred_at).getTime()) < 3600000
      );
      if (cluster.length >= 2) {
        cluster.forEach((c) => used.add(c.id));
        clusters.push({
          count: cluster.length,
          center: e.city || e.country || "Unknown",
          time: new Date(e.occurred_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
        });
      }
    }

    return { last6h: last6h.length, last24h: last24h.length, clusters };
  }, [missileEvents]);

  if (!isPremium && !embedded) {
    return (
      <section className="border-b border-border">
        <div className="container mx-auto px-4 py-12">
          <div className="relative overflow-hidden rounded-xl border border-border bg-card p-8 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-danger/5 to-transparent" />
            <div className="relative">
              <Lock className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h2 className="mb-2 text-2xl font-bold text-foreground">🎯 Missile Early Warning</h2>
              <p className="mb-6 text-muted-foreground max-w-lg mx-auto">
                Estimated blast radius, shelter time, and salvo detection for active missile threats.
              </p>
              <Button
                onClick={() => setPricingOpen(true)}
                className="bg-primary text-primary-foreground"
              >
                <Zap className="mr-2 h-4 w-4" /> Unlock Missile Warning
              </Button>
            </div>
          </div>
        </div>
        <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
      </section>
    );
  }

  const Wrapper = embedded ? "div" : "section";
  const wrapperProps = embedded ? {} : { id: "missile-warning", className: "border-b border-border" };

  return (
    <Wrapper {...wrapperProps}>
      <div className={embedded ? "" : "container mx-auto px-4 py-8"}>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Target className="h-6 w-6 text-destructive" /> Missile Early Warning
          </h2>
          <p className="text-sm text-muted-foreground">
            Active missile/drone threats, estimated blast radius, and shelter times
          </p>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="mb-6 grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border border-border bg-card p-4 text-center">
              <div className="font-mono text-xs uppercase text-muted-foreground">Last 6 Hours</div>
              <div className="text-3xl font-black text-destructive">{stats.last6h}</div>
              <div className="text-xs text-muted-foreground">missile/drone events</div>
            </div>
            <div className="rounded-lg border border-border bg-card p-4 text-center">
              <div className="font-mono text-xs uppercase text-muted-foreground">Last 24 Hours</div>
              <div className="text-3xl font-black text-warning">{stats.last24h}</div>
              <div className="text-xs text-muted-foreground">missile/drone events</div>
            </div>
            <div className="rounded-lg border border-border bg-card p-4 text-center">
              <div className="font-mono text-xs uppercase text-muted-foreground">Salvos Detected</div>
              <div className="text-3xl font-black text-danger">{stats.clusters.length}</div>
              <div className="text-xs text-muted-foreground">coordinated attacks (6h)</div>
            </div>
          </div>
        )}

        {/* Salvo Clusters */}
        {stats && stats.clusters.length > 0 && (
          <div className="mb-6 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
            <h3 className="mb-2 flex items-center gap-2 font-semibold text-destructive">
              <AlertTriangle className="h-4 w-4" /> Active Salvo Alerts
            </h3>
            <div className="space-y-2">
              {stats.clusters.map((c, i) => (
                <div key={i} className="flex items-center justify-between rounded bg-background px-3 py-2 border border-border">
                  <span className="text-sm text-foreground">
                    <strong>{c.count} impacts</strong> near {c.center}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">{c.time}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Missile Profiles Reference */}
        <div className="mb-6 rounded-lg border border-border bg-card p-4">
          <h3 className="mb-3 font-mono text-xs uppercase text-muted-foreground">
            Missile / Drone Profiles — Estimated Parameters
          </h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {MISSILE_PROFILES.map((m) => (
              <div key={m.type} className="rounded border border-border bg-background p-3">
                <p className="font-semibold text-foreground text-sm">{m.type}</p>
                <div className="mt-2 space-y-1 text-xs text-muted-foreground font-mono">
                  <div className="flex justify-between">
                    <span>Range</span>
                    <span className="text-foreground">{m.range} km</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Speed</span>
                    <span className="text-foreground">{m.speed} km/h</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Blast Radius</span>
                    <span className="text-destructive">{m.blastRadius} km</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Shelter Time</span>
                    <span className="text-warning">{m.shelterTime}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Events */}
        {missileEvents.length > 0 ? (
          <div className="rounded-lg border border-border bg-card p-4">
            <h3 className="mb-3 font-mono text-xs uppercase text-muted-foreground">
              Recent Missile/Drone Events ({missileEvents.length})
            </h3>
            <div className="space-y-2">
              {missileEvents.slice(0, 10).map((e) => {
                const hoursAgo = ((Date.now() - new Date(e.occurred_at).getTime()) / 3600000).toFixed(1);
                return (
                  <div key={e.id} className="flex items-center justify-between rounded border border-border/50 bg-background px-3 py-2">
                    <div className="flex items-center gap-3">
                      <Badge
                        variant="outline"
                        className="font-mono text-xs uppercase"
                        style={{
                          borderColor: e.event_type === "missile" ? "hsl(var(--destructive))" : "hsl(var(--warning))",
                          color: e.event_type === "missile" ? "hsl(var(--destructive))" : "hsl(var(--warning))",
                        }}
                      >
                        {e.event_type}
                      </Badge>
                      <span className="text-sm text-foreground">{e.title.slice(0, 50)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono">
                      <Clock className="h-3 w-3" />
                      {hoursAgo}h ago
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-border bg-card/50 p-12 text-center">
            <Target className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-muted-foreground">No active missile/drone threats detected</p>
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default MissileEarlyWarning;
